/**
 * M4.6 (F30, RET-227): search opens in place.
 * Loaded only when Theme settings, Launch features, "Search opens in place" is on
 * (sections/header.liquid). The header icon stays a real link to /search: without this
 * script, or if showModal is missing, it navigates as before.
 * Predictive results come from the predictive-search section through the Section
 * Rendering API; one request in flight, stale responses dropped.
 */
class SearchDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.idle = this.querySelector('[data-search-idle]');
    this.status = this.querySelector('[data-search-status]');
    if (!this.dialog || !this.input || typeof this.dialog.showModal !== 'function') return;

    // Theme editor re-renders reconnect the element: drop the old document listeners.
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-search-open]');
      if (!opener) return;
      event.preventDefault();
      this.open(opener);
    }, { signal });

    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.dialog.close(), { signal });
    // Backdrop: the dialog itself is the target when the click misses the panel.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    }, { signal });
    this.dialog.addEventListener('close', () => this.#onClose(), { signal });

    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.#search(), 250);
    }, { signal });

    this.dialog.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.controller?.abort();
  }

  open(trigger) {
    if (this.dialog.open) return;
    this.trigger = trigger || null;
    // The phone menu has no search icon, but never stack two modals.
    const mobileNav = document.getElementById('MobileNav');
    if (mobileNav?.open) mobileNav.close();
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    this.input.focus();
    this.input.select();
  }

  #onClose() {
    document.body.style.overflow = '';
    clearTimeout(this.timer);
    this.controller?.abort();
    if (this.trigger && this.trigger.isConnected) this.trigger.focus();
  }

  #links() {
    return [...this.results.querySelectorAll('[data-search-result]')];
  }

  /* Arrow Down from the input goes to the first result; Up and Down move between result
     links; Up from the first returns to the input. Escape is the dialog's own. */
  #onKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const links = this.#links();
    if (!links.length) return;
    const index = links.indexOf(document.activeElement);
    if (document.activeElement === this.input) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        links[0].focus();
      }
      return;
    }
    if (index === -1) return;
    event.preventDefault();
    if (event.key === 'ArrowDown') links[Math.min(index + 1, links.length - 1)].focus();
    else if (index === 0) this.input.focus();
    else links[index - 1].focus();
  }

  #showIdle() {
    this.controller?.abort();
    this.results.replaceChildren();
    if (this.idle) this.idle.hidden = false;
    this.status.textContent = '';
  }

  async #search() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.#showIdle();
      return;
    }
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,page',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });
    try {
      const res = await fetch(`${this.dataset.url}?${params.toString()}`, { signal: controller.signal });
      if (!res.ok) throw new Error(`Predictive search ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const root = doc.querySelector('[data-predictive-results]');
      if (!root) throw new Error('Empty predictive search response');
      if (controller.signal.aborted || this.input.value.trim() !== query) return;
      if (this.idle) this.idle.hidden = true;
      this.results.replaceChildren(document.importNode(root, true));
      this.status.textContent = root.dataset.status || '';
    } catch (error) {
      if (error.name === 'AbortError' || this.input.value.trim() !== query) return;
      this.#showError(query);
    }
  }

  /* Never a blank panel: a "See all results" link for the typed query. */
  #showError(query) {
    if (this.idle) this.idle.hidden = true;
    const link = document.createElement('a');
    link.className = 'predictive-search__see-all';
    link.setAttribute('data-search-result', '');
    link.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&type=product`;
    link.textContent = (this.dataset.seeAllTemplate || '').replace('[terms]', () => query);
    const wrap = document.createElement('div');
    wrap.className = 'predictive-search';
    wrap.append(link);
    this.results.replaceChildren(wrap);
    this.status.textContent = this.dataset.unavailable || '';
  }
}

if (!customElements.get('search-dialog')) {
  customElements.define('search-dialog', SearchDialog);
}
